import { PowerType } from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import i18next from 'i18next'

import { type CivilDate, compareCivilDate } from '~/models/civilDate'
import { unitPeriodMixMessage } from '~/pages/AdminOcctoMaster/tabs/_common/models/powerTypeIntegrity'

/**
 * 電源ユニットの入力中の E1 (同じ系統コードの他のユニットと、期間が重なって電源種別が違う)。
 * backend の E1 (保存時の最後の防御) と同じ判定を、保存を押す前に欄へ出すために画面でも行う
 * (電源ユニットマスタ 2b / AC-PUM-2B-6、DE 判断 2026-10-27)。
 */

/** 判定に使う他のユニット (一覧の行の一部)。 */
export type OverlapCandidateUnit = {
  id: string
  unitName: string
  /** 系統コード (広域機関の発電所) の id。未設定は undefined。 */
  plantMasterId: string | undefined
  powerType: PowerType
  validFrom: CivilDate
  validTo: CivilDate | undefined
}

/** 入力中のユニット。 */
export type OverlapInput = {
  /** 選んだ系統コードの id。未設定は ''。 */
  plantMasterId: string
  powerType: PowerType
  validFrom: CivilDate | null | undefined
  validTo: CivilDate | null | undefined
}

/** 編集を始めた時のユニット (詳細ページの編集だけ)。Q9 の例外の判定に使う。 */
export type OverlapOriginal = {
  plantMasterId: string
  powerType: PowerType
}

/** 2 つの有効期間が、期間全体で 1 日でも重なるか (終了日は含む。終了日なしは無期限)。 */
export function periodsOverlap(
  a: { validFrom: CivilDate; validTo: CivilDate | null | undefined },
  b: { validFrom: CivilDate; validTo: CivilDate | null | undefined },
): boolean {
  const start =
    compareCivilDate(a.validFrom, b.validFrom) >= 0 ? a.validFrom : b.validFrom
  const ends = [a.validTo, b.validTo].filter(
    (end): end is CivilDate => end !== null && end !== undefined,
  )
  if (ends.length === 0) return true
  const end = ends.reduce((earliest, value) =>
    compareCivilDate(value, earliest) < 0 ? value : earliest,
  )
  return compareCivilDate(start, end) <= 0
}

/**
 * 入力中のユニットと、期間が重なって電源種別が違う他のユニット (自分以外・同じ系統コード)。
 * 次のときは空 (エラーにしない):
 * - 系統コード未設定、電源種別が未選択、有効開始日が未入力
 * - 詳細ページの編集で、系統コードを変えずに電源種別だけを変える (Q9: 保存時に「他のユニットも変わる」確認を出す。
 *   backend の Q9 の条件と同じ: 系統コード不変・種別だけ変更)
 */
export function findPowerTypeOverlaps(args: {
  input: OverlapInput
  selfId: string | undefined
  original: OverlapOriginal | undefined
  units: readonly OverlapCandidateUnit[]
}): OverlapCandidateUnit[] {
  const { input, selfId, original, units } = args
  if (input.plantMasterId === '' || input.powerType === PowerType.UNSPECIFIED)
    return []
  if (input.validFrom === null || input.validFrom === undefined) return []
  if (
    original !== undefined &&
    original.plantMasterId === input.plantMasterId &&
    original.powerType !== input.powerType
  ) {
    return []
  }
  const mine = { validFrom: input.validFrom, validTo: input.validTo }
  return units.filter(
    (other) =>
      other.id !== selfId &&
      other.plantMasterId === input.plantMasterId &&
      other.powerType !== input.powerType &&
      periodsOverlap(mine, other),
  )
}

/**
 * 電源種別の欄に出す文言 (入力中の E1)。保存時に backend が止めた時と同じ文言 (`unitPeriodMixMessage`)。
 * 重なる他のユニットが複数なら「、」で区切る。
 */
export function powerTypeOverlapMessage(args: {
  plantCode: string
  overlaps: readonly OverlapCandidateUnit[]
}): string {
  return unitPeriodMixMessage(
    i18next.t.bind(i18next),
    args.plantCode,
    args.overlaps.map((other) => ({
      unitName: other.unitName,
      powerType: other.powerType,
    })),
  )
}
